import { translateBackend } from '@assets/i18n/config';
import type { PortfolioReportQueryFilter } from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import type { DashboardPortfolioReport, PortfolioReport } from '../types';
import { getPortfolioName } from './portfolioName';

export type PortfolioSearchOption = {
  id: string;
  label: string;
  searchValue?: string;
};

export const TOOLBAR_SEARCH_MIN_CHARS = 1;
export const PORTFOLIO_SEARCH_SUGGESTION_PAGE_SIZE = 12;
export const EXPOSURE_SEARCH_SUGGESTION_PAGE_SIZE = 12;

type PortfolioReportSearchRow = PortfolioReport | DashboardPortfolioReport;

type FinancialProductSearchRow = {
  id: string;
  ticker?: string | null;
  translatedName?: TranslatedString | null;
};

export function buildPortfolioReportsSearchVariables(
  organizationId: string,
  query: string,
  canReadRepCode = false,
) {
  const trimmed = query.trim();
  const filter: PortfolioReportQueryFilter & { searchText?: string } = {
    organizationId,
    searchText: trimmed,
  };
  return {
    input: {
      filter,
      pagination: {
        perPage: PORTFOLIO_SEARCH_SUGGESTION_PAGE_SIZE,
        sortField: 'updatedAt',
        sortDesc: true,
      },
    },
    canReadRepCode,
  };
}

export function getPortfolioReportSearchValue(
  report: PortfolioReportSearchRow,
): string {
  const label = getPortfolioName(report);
  const obj = report.object;
  if (!obj) return label;

  if (report.modelPortfolioType === 'GOAL') {
    const goalObject = obj as {
      name?: string;
      user?: {
        firstName?: string;
        lastName?: string;
        entityName?: string;
      };
    };
    const user = goalObject.user;
    if (user?.firstName) {
      return `${user.firstName} ${user.lastName || ''}`.trim();
    }
    if (user?.entityName) return user.entityName;
    if (goalObject.name) return goalObject.name;
    return label;
  }

  const subAccountObject = obj as {
    account?: {
      type?: string;
      user?: {
        firstName?: string;
        lastName?: string;
        entityName?: string;
      };
    };
    goal?: { name?: string };
  };
  const user = subAccountObject.account?.user;
  if (user?.firstName) {
    return `${user.firstName} ${user.lastName || ''}`.trim();
  }
  if (user?.entityName) return user.entityName;
  if (subAccountObject.goal?.name) return subAccountObject.goal.name;
  return label;
}

export function mapPortfolioReportSearchHits(
  reports: readonly PortfolioReportSearchRow[] | null | undefined,
): PortfolioSearchOption[] {
  return (reports ?? [])
    .map((report) => ({
      id: report.id,
      label: getPortfolioName(report),
      searchValue: getPortfolioReportSearchValue(report),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildExposureSearchVariables(
  organizationId: string,
  query: string,
) {
  const trimmed = query.trim();
  return {
    input: {
      filter: {
        organizationId,
        searchText: trimmed,
      },
      pagination: {
        perPage: EXPOSURE_SEARCH_SUGGESTION_PAGE_SIZE,
        sortField: 'ticker',
      },
    },
  };
}

export function mapFinancialProductSearchHits(
  products: readonly FinancialProductSearchRow[] | null | undefined,
): PortfolioSearchOption[] {
  return (products ?? [])
    .map((product) => {
      const ticker = product.ticker?.trim();
      const translatedName = translateBackend(product.translatedName);
      const label = ticker || translatedName || product.id;
      return {
        id: product.id,
        label,
        searchValue: ticker || label,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
